// # Customer Information Component for Payment Registration
import React from 'react';
import { 
  FaUserAlt, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaGlobe, 
  FaUserTie
} from 'react-icons/fa';
import { nationalityData } from '../../../../../../utils/nationality_data';
// # Import the component-specific CSS
import './pay_customer.css';

// # Customer Information component that handles user selection and personal details
const PayCustomer = ({ 
  formData, 
  handleChange, 
  users, 
  userSearch, 
  setUserSearch, 
  sortOrder, 
  setSortOrder 
}) => {
  // # Filter and sort users based on search term and sort order
  const getFilteredUsers = () => {
    return users
      .filter(user => {
        const searchTerm = userSearch.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return fullName.includes(searchTerm) || 
               user.email?.toLowerCase().includes(searchTerm);
      })
      .sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return sortOrder === 'asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      });
  };

  return (
    <div className="pay_customer_section">
      <div className="pay_customer_section-header">
        <FaUserAlt />
        <h2>Customer Information</h2>
      </div>

      <div className="pay_customer_form-group">
        <label>
          <FaUserTie className="pay_customer_input-icon" />
          Select User
        </label>
        <div className="pay_customer_user-select-container">
          <div className="pay_customer_user-search">
            <input
              type="text"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              className="pay_customer_sort-btn"
            >
              {sortOrder === 'asc' ? '↓' : '↑'}
            </button>
          </div>
          <select 
            name="userId" 
            value={formData.userId}
            onChange={handleChange}
            className="pay_customer_user-select"
          >
            <option value="">Select User (Optional)</option>
            {getFilteredUsers().map(user => (
              <option key={user.uid} value={user.uid}>
                {user.firstName} {user.lastName} - {user.email}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="pay_customer_form-group">
        <label>
          <FaGlobe className="pay_customer_input-icon" />
          Nationality
        </label>
        <select 
          name="nationality" 
          value={formData.nationality}
          onChange={handleChange}
          required
          className="pay_customer_nationality-select"
        >
          <option value="">Select Nationality</option>
          
          {/* Arab Countries */}
          <optgroup label="Arab Countries">
            {Object.entries(nationalityData)
              .filter(([_, data]) => data.searchKeys.includes('arab'))
              .sort((a, b) => a[1].name.localeCompare(b[1].name))
              .map(([key, data]) => (
                <option key={key} value={data.name}>
                  {data.flag} {data.name}
                </option>
              ))}
          </optgroup>

          {/* Asian Countries */}
          <optgroup label="Asia">
            {Object.entries(nationalityData)
              .filter(([_, data]) => data.searchKeys.includes('asia') && !data.searchKeys.includes('arab'))
              .sort((a, b) => a[1].name.localeCompare(b[1].name))
              .map(([key, data]) => (
                <option key={key} value={data.name}>
                  {data.flag} {data.name}
                </option>
              ))}
          </optgroup>

          {/* European Countries */}
          <optgroup label="Europe">
            {Object.entries(nationalityData)
              .filter(([_, data]) => data.searchKeys.includes('europe') && !data.searchKeys.includes('asia'))
              .sort((a, b) => a[1].name.localeCompare(b[1].name))
              .map(([key, data]) => (
                <option key={key} value={data.name}>
                  {data.flag} {data.name}
                </option>
              ))}
          </optgroup>

          {/* Americas */}
          <optgroup label="Americas">
            {Object.entries(nationalityData)
              .filter(([_, data]) => data.searchKeys.includes('americas'))
              .sort((a, b) => a[1].name.localeCompare(b[1].name))
              .map(([key, data]) => (
                <option key={key} value={data.name}>
                  {data.flag} {data.name}
                </option>
              ))}
          </optgroup>

          {/* African Countries (Non-Arab) */}
          <optgroup label="Africa (Non-Arab)">
            {Object.entries(nationalityData)
              .filter(([_, data]) => data.searchKeys.includes('africa') && !data.searchKeys.includes('arab'))
              .sort((a, b) => a[1].name.localeCompare(b[1].name))
              .map(([key, data]) => (
                <option key={key} value={data.name}>
                  {data.flag} {data.name}
                </option>
              ))}
          </optgroup>

          {/* Oceania */}
          <optgroup label="Oceania">
            {Object.entries(nationalityData)
              .filter(([_, data]) => data.searchKeys.includes('oceania'))
              .sort((a, b) => a[1].name.localeCompare(b[1].name))
              .map(([key, data]) => (
                <option key={key} value={data.name}>
                  {data.flag} {data.name}
                </option>
              ))}
          </optgroup>
        </select>
      </div>

      <div className="pay_customer_form-group">
        <label>
          <FaUserAlt className="pay_customer_input-icon" />
          Name
        </label>
        <input 
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="pay_customer_form-group">
        <label>
          <FaEnvelope className="pay_customer_input-icon" />
          Email
        </label>
        <input 
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>

      <div className="pay_customer_form-group">
        <label>
          <FaPhoneAlt className="pay_customer_input-icon" />
          Phone (with country code)
        </label>
        <input 
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          placeholder="Enter phone number with country code"
        />
      </div>
    </div>
  );
};

export default PayCustomer;
